/** Admin: monthly fuel-refund totals per driver with paid toggle. */
import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, CheckCircle2, CircleDollarSign } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const prevMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export const MonthlyRefundsPanel = () => {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(prevMonth);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async (m) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/fuel-refunds/summary", { params: { month: m } });
      setRows(data.rows || []);
    } catch {
      toast.error("Učitavanje mjesečnih povrata nije uspjelo.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load(month);
  }, [open, month]);

  const togglePaid = async (r) => {
    setBusyId(r.driver_id);
    try {
      await api.post("/admin/fuel-refunds/mark-paid", {
        driver_id: r.driver_id, month, paid: !r.paid,
      });
      toast.success(!r.paid
        ? `Povrat za ${r.driver_name} označen kao isplaćen — vozač sada vidi "Isplaćeno".`
        : `Oznaka isplate uklonjena za ${r.driver_name}.`);
      await load(month);
    } catch {
      toast.error("Promjena nije uspjela.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mb-4 rounded-xl border border-white/10 bg-[#0d0d0d] overflow-hidden" data-testid="monthly-refunds-panel">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02] transition text-left"
        data-testid="monthly-refunds-toggle"
      >
        {open ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
        <CircleDollarSign className="w-4 h-4 text-[#C5A059]" />
        <span className="text-sm text-white">Mjesečni povrati goriva</span>
        <span className="text-xs text-zinc-500">označi isplaćeno po vozaču</span>
      </button>
      {open && (
        <div className="border-t border-white/5 p-4">
          <input
            type="month"
            value={month}
            onChange={(e) => e.target.value && setMonth(e.target.value)}
            className="bg-black/40 border border-white/10 px-3 py-1.5 rounded-lg text-sm mb-3 focus:outline-none focus:border-[#C5A059]/60"
            data-testid="monthly-refunds-month"
          />
          {loading ? (
            <p className="text-sm text-zinc-500 py-4">Učitavanje…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4">Nema odobrenih računa za odabrani mjesec.</p>
          ) : (
            <div className="divide-y divide-white/5">
              {rows.map((r) => (
                <div key={r.driver_id} className="flex items-center gap-3 py-2.5" data-testid={`refund-row-${r.driver_id}`}>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-white">{r.driver_name}</span>
                    <span className="text-xs text-zinc-500 ml-2">{r.receipts} rač.</span>
                  </div>
                  <span className="text-sm text-[#C5A059] font-semibold whitespace-nowrap">€{r.refund_eur.toFixed(2)}</span>
                  <button
                    onClick={() => togglePaid(r)}
                    disabled={busyId === r.driver_id}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition disabled:opacity-50 whitespace-nowrap
                      ${r.paid
                        ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400"
                        : "border-white/15 text-zinc-300 hover:bg-white/10"}`}
                    data-testid={`refund-paid-btn-${r.driver_id}`}
                  >
                    {r.paid ? (
                      <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Isplaćeno</span>
                    ) : "Označi isplaćeno"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
